import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import PrintifyProducts from '../components/PrintifyProducts';
import PrintifySidebarManager from '../components/PrintifySidebarManager';
import LowerSidebarManager from './LowerSidebarManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsciousCommerceAdmin = () => {
  const { token, isAdminMode, user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentView, setCurrentView] = useState('products'); // 'products' or 'sidebar'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    currency: 'USD',
    printify_url: '',
    image_url: '',
    category: '',
    tags: '',
    placement_types: ['filler', 'sidebar'],
    priority: 0,
    size_preference: 'small_rectangle'
  });

  const [affiliateAdForm, setAffiliateAdForm] = useState({
    title: '',
    description: '',
    affiliate_url: '',
    image_url: '',
    size: '300x250',
    mobile_size: '',
    tablet_size: '',
    device_targeting: 'all',
    placement_type: 'in_feed',
    priority: 1
  });
  
  // Printify import state
  const [importStatus, setImportStatus] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (isAdminMode) {
      fetchProducts();
      fetchCategories();
      fetchImportStatus();
    }
  }, [isAdminMode]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/conscious-commerce/products?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/conscious-commerce/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchImportStatus = async () => {
    try {
      const response = await axios.get(`${API}/conscious-commerce/import-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImportStatus(response.data);
    } catch (error) {
      console.error('Error fetching import status:', error);
    }
  };

  const importFromPrintify = async () => {
    setImporting(true);
    try {
      const response = await axios.post(`${API}/conscious-commerce/import-printify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`✅ ${response.data.message}`);
      fetchProducts(); // Refresh products list
      fetchImportStatus(); // Update import status
    } catch (error) {
      console.error('Error importing from Printify:', error);
      alert('❌ Error importing products: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        priority: parseInt(formData.priority)
      };

      if (editingProduct) {
        // Update existing product
        await axios.put(`${API}/conscious-commerce/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Product updated successfully!');
      } else {
        // Create new product
        await axios.post(`${API}/conscious-commerce/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Product created successfully!');
      }

      // Reset form and refresh products
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      currency: product.currency,
      printify_url: product.printify_url,
      image_url: product.image_url,
      category: product.category,
      tags: product.tags.join(', '),
      placement_types: product.placement_types,
      priority: product.priority,
      size_preference: product.size_preference || 'normal'
    });
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await axios.delete(`${API}/conscious-commerce/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product');
    }
  };

  const toggleActive = async (product) => {
    try {
      await axios.put(`${API}/conscious-commerce/products/${product.id}`, {
        is_active: !product.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (error) {
      console.error('Error toggling product status:', error);
      alert('Error updating product status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      currency: 'USD',
      printify_url: '',
      image_url: '',
      category: '',
      tags: '',
      placement_types: ['filler', 'sidebar'],
      priority: 0,
      size_preference: 'small_rectangle'
    });
    setEditingProduct(null);
    setShowAddForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'placement_types') {
      const currentTypes = formData.placement_types;
      if (checked) {
        setFormData({ ...formData, placement_types: [...currentTypes, value] });
      } else {
        setFormData({ ...formData, placement_types: currentTypes.filter(t => t !== value) });
      }
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
  };

  if (!isAdminMode) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">This feature is restricted to administrators only.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">🛒 Conscious Commerce Admin</h1>
        {currentView === 'products' && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            {showAddForm ? 'Cancel' : '+ Add Product'}
          </button>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="flex space-x-1 mb-8 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => {
            setCurrentView('products');
            setShowAddForm(false);
          }}
          className={`flex-1 px-6 py-3 rounded-md text-sm font-medium transition-all ${
            currentView === 'products'
              ? 'bg-white text-purple-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📦 Products & Categories
        </button>
        <button
          onClick={() => {
            setCurrentView('sidebar');
            setShowAddForm(false);
          }}
          className={`flex-1 px-6 py-3 rounded-md text-sm font-medium transition-all ${
            currentView === 'sidebar'
              ? 'bg-white text-purple-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          🎛️ Top Sidebar (2 items)
        </button>
        <button
          onClick={() => {
            setCurrentView('lower-sidebar');
            setShowAddForm(false);
          }}
          className={`flex-1 px-6 py-3 rounded-md text-sm font-medium transition-all ${
            currentView === 'lower-sidebar'
              ? 'bg-white text-purple-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          🛒 Lower Sidebar (11 items)
        </button>
      </div>

      {/* Products View */}
      {currentView === 'products' && (
        <>
          {/* Printify Import Section */}
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-blue-800 mb-4">📦 Import from Printify</h2>
            
            {importStatus && (
              <div className="mb-4 p-3 bg-white rounded-lg border">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800">
                      Printify Products: {importStatus.printify_products_count}
                    </p>
                    {importStatus.last_import_date && (
                      <p className="text-sm text-gray-600">
                        Last import: {new Date(importStatus.last_import_date).toLocaleDateString()} 
                        by {importStatus.last_imported_by}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={importFromPrintify}
                    disabled={importing}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {importing ? 'Importing...' : 'Re-import from Printify'}
                  </button>
                </div>
              </div>
            )}
            
            {!importStatus && (
              <div className="text-center">
                <p className="text-blue-700 mb-4">Import Printify products to start managing your store inventory.</p>
                <button
                  onClick={importFromPrintify}
                  disabled={importing}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {importing ? 'Importing...' : 'Import from Printify'}
                </button>
              </div>
            )}
            
            <p className="text-xs text-blue-600 mt-3">
              💡 After import, you can edit product links to ensure they point to the correct store pages.
            </p>
          </div>

          {/* Add/Edit Product Form */}
          {showAddForm && (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g., Mindfulness T-Shirt"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  name="placement_types"
                  value="sidebar_extra"
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="filler">Filler Posts (in main feed)</option>
                  <option value="sidebar">Top Sidebar Section</option>  
                  <option value="sidebar_extra">Lower Sidebar (11 items max)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  placeholder="29.99"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority (0-100)
                </label>
                <input
                  type="number"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  placeholder="0 (higher numbers show first)"
                />
              </div>

              {/* Size options - ONLY for feed placement, NOT for sidebar */}
              {formData.placement_types.includes('filler') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Feed Display Size (applies to main feed only)
                  </label>
                  <select
                    name="size_preference"
                    value={formData.size_preference}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="small_rectangle">Small Rectangle</option>
                    <option value="large_banner">Large Banner</option>
                    <option value="thin_banner">Thin Banner</option>
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows="3"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                placeholder="Describe the consciousness-aligned product..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Printify Product URL *
              </label>
              <input
                type="url"
                name="printify_url"
                value={formData.printify_url}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                placeholder="https://modernhumanbeing.printify.me/product/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Image URL *
              </label>
              <input
                type="url"
                name="image_url"
                value={formData.image_url}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                placeholder="mindfulness, consciousness, wellness"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Where to Display
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="placement_types"
                    value="filler"
                    checked={formData.placement_types.includes('filler')}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Main Feed (with size options)
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="placement_types"
                    value="sidebar"
                    checked={formData.placement_types.includes('sidebar')}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Sidebar (standard size)
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="placement_types"
                    value="sidebar_extra"
                    checked={formData.placement_types.includes('sidebar_extra')}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Lower Sidebar (standard size)
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                {editingProduct ? 'Update Product' : 'Create Product'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Products List */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Current Products ({products.length})</h2>
        </div>

        <div className="divide-y divide-gray-200">
          {products.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No products added yet. Click "Add Product" to get started.
            </div>
          ) : (
            products.map((product) => (
              <div key={product.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {product.title}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        product.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {product.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                        {product.category}
                      </span>
                      {product.priority > 0 && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          Priority: {product.priority}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-2">{product.description}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>${product.price} {product.currency}</span>
                      <span>Displays: {product.placement_types.join(', ')}</span>
                      {product.tags.length > 0 && (
                        <span>Tags: {product.tags.join(', ')}</span>
                      )}
                    </div>
                    
                    <a
                      href={product.printify_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-800 text-sm mt-1 inline-block"
                    >
                      View on Printify →
                    </a>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    {product.image_url && (
                      <img
                        src={product.image_url}
                        alt={product.title}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    )}
                    
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => toggleActive(product)}
                        className={`px-3 py-1 text-xs rounded ${
                          product.is_active
                            ? 'bg-red-100 text-red-800 hover:bg-red-200'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {product.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      
                      <button
                        onClick={() => handleEdit(product)}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                      >
                        Edit
                      </button>
                      
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="px-3 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

          {/* MHB Merchandise Section */}
          <div className="mt-8">
            <PrintifyProducts adminMode={true} />
          </div>
        </>
      )}

      {/* Sidebar Manager View */}
      {currentView === 'sidebar' && (
        <PrintifySidebarManager />
      )}

      {/* Lower Sidebar Manager View */}
      {currentView === 'lower-sidebar' && (
        <LowerSidebarManager />
      )}
    </div>
  );
};

export default ConsciousCommerceAdmin;